import { useRouter } from 'next/router';
import { useQueryState } from 'nuqs';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ToastTypeVariants } from '@/context/ToastContext';
import {
  DEFAULT_FINGERPRINT_MAX_AGE_DAYS,
  LOGOUT_FINGERPRINT_MAX_AGE_DAYS,
  clearStaleTusFingerprints,
} from '@/helpers/clearStaleTusFingerprints';
import isBrowser from '@/helpers/isBrowser';
import useNoodleApi from '@/hooks/useNoodleApi';
import { logError } from '@/providers/ErrorTracking';
import * as tsClient from '@/tsClient';
import { type UseEffectDestructor } from '@/typings/utility';

import AuthContext, { type AuthContextType } from './AuthContext';
import { type AuthUser, type Profile } from './constants';
import createJWTCookie from './createJWTCookie';
import decodeToken from './decodeToken';
import getCookieUser from './getCookieUser';
import IsOnBehalfOfBar from './IsOnBehalfOfBar';
import validUser from './validUser';

// trouble making value required here, but optional in general, so can't just use IframeEvent<>
type IframeMessageEvent = {
  data: {
    message: string;
    value: {
      jwtCookie: string;
      parentHost: string;
    };
  };
};

const logoutPaths = ['/auth/logout'];

const calcIsCreator = (newUser: AuthUser | null): boolean => {
  if (!newUser) {
    return false;
  }
  if (typeof newUser.isCreator === 'boolean') {
    return newUser.isCreator;
  }
  return false;
};

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [_qsAuthToken, setQsAuthToken] = useQueryState('authToken');
  // getting authToken from useQueryState isn't available for one more render causing the wrong token to be used briefly.
  const qsAuthToken = typeof router.query.authToken === 'string' ? router.query.authToken : null;

  const currentUserIdRef = useRef(user?.id ?? null);
  currentUserIdRef.current = user?.id ?? null;

  // Track previous user for logout detection
  const prevUserRef = useRef<AuthUser | null>(null);

  // We can get multiple simultaneous 'client-cookie' actions with the same token (multiple iframes in chain).
  // Need to prevent creating a new user when we get the same token again so useEffect(fn, [user]) doesn't loop.
  // If we useState to hold the token, we wouldn't see the handled token till the next render.
  // Hold the currentToken in useRef instead so it's immediately avallable to all.
  const currentToken = useRef<string | null>(null);

  const [isContextInitialized, setIsContextInitialized] = useState(false);
  const { getData: getProfile } = useNoodleApi(tsClient.my.getProfile);
  const { getData: updateProfileFn } = useNoodleApi(tsClient.my.updateProfile, {
    toastOnError: true,
    toastSuccessMessage: () => [ToastTypeVariants.SUCCESS, 'Profile successfully updated.'],
  });

  const wrapSetUser = useCallback(
    (newUser: AuthUser | null) => {
      if (!newUser) {
        setUser(null);
        setProfile(null);
      } else {
        setUser({
          exp: newUser.exp || 1000,
          id: newUser.id,
          isAnonymous: newUser.isAnonymous ?? false,
          isComplete: newUser.isComplete ?? false,
          isCreator: calcIsCreator(newUser),
          isOnBehalfOf: newUser.isOnBehalfOf,
          token: newUser.token,
        });
      }
    },
    [setUser],
  );

  useEffect(() => {
    if (user && user.id && !user.isAnonymous && !logoutPaths.includes(router.pathname)) {
      const loadProfile = async (): Promise<void> => {
        const thisUserId = user?.id;
        const response = await getProfile();
        if (thisUserId === currentUserIdRef.current) {
          if (response.data) {
            setProfile(response.data);
          } else {
            setProfile(null);
          }
        }
      };
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, getProfile]);

  useEffect((): UseEffectDestructor => {
    const eventHandler = (event: IframeMessageEvent): void => {
      if (event?.data?.message === 'client-cookie') {
        const { jwtCookie, parentHost } = event.data.value;

        // Hold the currentToken in useRef instead of useState.
        // If we useState, we wouldn't see the handled token till the next render, with useRef multiple simultaneous actions see the token immediately.
        if (jwtCookie === currentToken?.current) {
          return;
        }
        currentToken.current = jwtCookie;
        let jwt: AuthUser | null = null;

        try {
          jwt = decodeToken(jwtCookie);
        } catch (error) {
          logError(error, { from: 'client-cookie-event' });
        }

        jwt = validUser(jwt);
        if (parentHost) {
          window.parentHost = parentHost;
        }

        if (jwtCookie) {
          window.httpToken = jwtCookie;
        }
        wrapSetUser(jwt);
      }
    };

    window.addEventListener('message', eventHandler, false);

    return (): void => {
      window.removeEventListener('message', eventHandler, false);
    };
  }, [wrapSetUser]);

  useEffect(() => {
    // Wait till the router is ready, so that authToken is known.
    if (router.isReady) {
      let newUser: AuthUser | null = null;
      if (qsAuthToken) {
        newUser = validUser(decodeToken(qsAuthToken));
        if (newUser) {
          createJWTCookie(qsAuthToken);
        }
      }
      if (!newUser) {
        newUser = validUser(getCookieUser());
      }
      wrapSetUser(newUser);
      setQsAuthToken(null, { history: 'replace' });
      setIsContextInitialized(true);
    }
  }, [wrapSetUser, qsAuthToken, router.isReady, setQsAuthToken]);

  const updateProfile = useCallback<AuthContextType['updateProfile']>(
    async (body) => {
      const response = await updateProfileFn(body);
      if (response.data) {
        setProfile(response.data);
      }
      return response;
    },
    [updateProfileFn],
  );

  // TUS fingerprint cleanup with logout detection
  useEffect(() => {
    if (!isContextInitialized || !isBrowser()) return;

    const prevUser = prevUserRef.current;
    const isLoggingOut = prevUser !== null && user === null;

    if (isLoggingOut) {
      clearStaleTusFingerprints({
        clearAllOnError: true,
        maxAgeDays: LOGOUT_FINGERPRINT_MAX_AGE_DAYS,
      });
    } else if (user) {
      clearStaleTusFingerprints({
        clearAllOnError: true,
        maxAgeDays: DEFAULT_FINGERPRINT_MAX_AGE_DAYS,
      });
    }

    prevUserRef.current = user;
  }, [isContextInitialized, user]);

  const value = useMemo(
    () => ({
      isContextInitialized,
      profile,
      setUser: wrapSetUser,
      updateProfile,
      user,
    }),
    [isContextInitialized, wrapSetUser, user, profile, updateProfile],
  );

  return (
    <AuthContext.Provider value={value}>
      <IsOnBehalfOfBar user={user} profile={profile} />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
